/**
 * Theming, read from the iframe's own URL.
 *
 * The embed snippet bakes the broker's choices into the src, so a widget is
 * configured entirely by its URL and holds no per-tenant state of its own. That
 * is what lets the widget documents be static files cached at the edge: two
 * brokers on the same theme get the same bytes.
 *
 * Every value is validated here rather than trusted. These parameters are
 * interpolated into CSS, and a page can put anything in a URL — an unvalidated
 * `accent` would be a CSS injection into an iframe that shares an origin with
 * our API.
 */

export type Theme = 'light' | 'dark';
export type Density = 'comfortable' | 'compact';

export interface WidgetConfig {
  embedKey: string;
  theme: Theme;
  accent: string;
  font: string;
  locale: string;
  density: Density;
  /**
   * IANA timezone every time and date in this widget is rendered in, from the
   * `tz` parameter.
   *
   * Separate from `locale` on purpose, because they answer different questions:
   * locale decides the *format* ("14 Aug" vs "Aug 14"), timezone decides the
   * *instant* a timestamp maps to. A broker in Mumbai serving a British
   * audience wants en-GB formatting on Asia/Kolkata times, and collapsing the
   * two into one parameter makes that unexpressible.
   */
  timeZone: string;
  /** The parent page's origin, forwarded so the API can check the allowlist. */
  referrer: string | null;
}

const DEFAULT_ACCENT = '#2563eb';
const DEFAULT_FONT =
  'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

/**
 * Colours are restricted to hex and the handful of functional notations that
 * cannot contain a semicolon, a brace or a url(). `accent=red;}body{display:none`
 * is a real thing a hostile embedder could try.
 */
const SAFE_COLOUR =
  /^(#[0-9a-f]{3,8}|(rgb|hsl)a?\([0-9a-z%.,\s/]+\)|[a-z]{3,20})$/i;

/**
 * A font-family value: names, quotes, spaces, commas and hyphens. No
 * parentheses, so url() and expression() cannot appear.
 */
const SAFE_FONT = /^[\w\s,'"-]{1,200}$/;

export interface ReadConfigOptions {
  /**
   * Zone used when `tz` is absent or invalid.
   *
   * Passed in by each widget rather than fixed here: the India corporate-event
   * widgets default to Asia/Kolkata because their audience and their exchanges
   * are both there, while the global widgets default to UTC, which is the only
   * defensible neutral for an audience we cannot locate.
   */
  defaultTimeZone?: string;
}

export function readConfig(options: ReadConfigOptions = {}): WidgetConfig {
  const params = new URLSearchParams(window.location.search);

  const rawTheme = params.get('theme');
  const theme: Theme =
    rawTheme === 'dark'
      ? 'dark'
      : rawTheme === 'auto'
        ? // 'auto' resolves once, at load. A widget that restyled itself
          // mid-session because the OS flipped to dark would be a surprise in
          // the middle of someone else's page.
          window.matchMedia?.('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : 'light';

  const accent = params.get('accent')?.trim() ?? '';
  const font = params.get('font')?.trim() ?? '';
  const density = params.get('density') === 'compact' ? 'compact' : 'comfortable';

  return {
    embedKey: params.get('key') ?? params.get('embed_key') ?? '',
    theme,
    accent: SAFE_COLOUR.test(accent) ? accent : DEFAULT_ACCENT,
    font: SAFE_FONT.test(font) ? font : DEFAULT_FONT,
    locale: normaliseLocale(params.get('locale')),
    density,
    timeZone: normaliseTimeZone(params.get('tz'), options.defaultTimeZone ?? 'UTC'),
    referrer: parentOrigin(),
  };
}

/**
 * An IANA timezone Intl will accept.
 *
 * Validated by construction rather than by pattern, for the same reason as the
 * locale: an unknown zone makes every DateTimeFormat throw, and one typo in a
 * broker's embed code would blank the whole widget instead of degrading to a
 * sensible default.
 */
function normaliseTimeZone(raw: string | null, fallback: string): string {
  const candidate = raw?.trim();
  if (!candidate) return fallback;
  try {
    new Intl.DateTimeFormat('en', { timeZone: candidate });
    return candidate;
  } catch {
    return fallback;
  }
}

/**
 * A BCP-47 tag Intl will accept.
 *
 * Validated by asking Intl itself rather than by pattern-matching: an invalid
 * tag makes every Intl constructor throw, which would blank the whole widget
 * over a typo in someone's embed code.
 */
function normaliseLocale(raw: string | null): string {
  const candidate = raw?.trim();
  if (!candidate) return 'en-GB';
  try {
    return Intl.NumberFormat.supportedLocalesOf([candidate])[0] ?? candidate;
  } catch {
    return 'en-GB';
  }
}

/**
 * The page this widget is embedded in.
 *
 * document.referrer is set by the browser from the embedding page and cannot be
 * spoofed by that page for itself, which is what makes it usable as the input to
 * the server-side domain allowlist. It is empty when the widget is opened
 * directly rather than in an iframe.
 */
function parentOrigin(): string | null {
  if (!document.referrer) return null;
  try {
    return new URL(document.referrer).origin;
  } catch {
    return null;
  }
}

/**
 * Apply the theme as CSS custom properties on :root.
 *
 * Custom properties rather than a stylesheet swap, so the accent colour can come
 * from a URL parameter without generating CSS text at runtime. The values were
 * validated above; setting them via the CSSOM means they are treated as property
 * values and can never escape into a new rule.
 */
export function applyTheme(config: WidgetConfig): void {
  const root = document.documentElement;
  const palette = config.theme === 'dark' ? DARK : LIGHT;

  for (const [name, value] of Object.entries(palette)) {
    root.style.setProperty(name, value);
  }
  root.style.setProperty('--accent', config.accent);
  root.style.setProperty('--font', config.font);

  const scale = config.density === 'compact' ? COMPACT : COMFORTABLE;
  for (const [name, value] of Object.entries(scale)) {
    root.style.setProperty(name, value);
  }

  root.dataset.theme = config.theme;
  root.lang = config.locale;
}

/**
 * Palettes.
 *
 * Deliberately neutral greys: a widget sits inside a broker's own design, and
 * the one thing it must not do is look like it belongs to someone else. The
 * accent is the only opinionated colour, and the broker chooses it.
 *
 * up/down are the exception — those are semantic, and a red/green convention is
 * what a trader expects. Both carry a text label or arrow as well as colour, so
 * the meaning survives colour-blindness.
 */
const LIGHT = {
  '--bg': '#ffffff',
  '--fg': '#0f172a',
  '--muted': '#64748b',
  '--border': '#e2e8f0',
  '--row-alt': '#f8fafc',
  '--up': '#047857',
  '--up-bg': '#ecfdf5',
  '--down': '#b91c1c',
  '--down-bg': '#fef2f2',
  '--skeleton': '#f1f5f9',
};

const DARK = {
  '--bg': '#0f172a',
  '--fg': '#f1f5f9',
  '--muted': '#94a3b8',
  '--border': '#1e293b',
  '--row-alt': '#152036',
  '--up': '#34d399',
  '--up-bg': '#053b2d',
  '--down': '#f87171',
  '--down-bg': '#3f1212',
  '--skeleton': '#1e293b',
};

const COMFORTABLE = {
  '--row-py': '10px',
  '--row-px': '12px',
  '--font-size': '14px',
  '--font-size-sm': '12px',
  '--gap': '10px',
};

const COMPACT = {
  '--row-py': '6px',
  '--row-px': '9px',
  '--font-size': '13px',
  '--font-size-sm': '11px',
  '--gap': '6px',
};
